import api from './api';

export interface InformacionCaja {
  codigoCaja: number;
  nombre: string;
  fechaApertura: string;
  fechaCierre: string | null;
  estado: string;
  usuarioResponsable: string;
  saldoInicial: number;
  totalIngresos: number;
  totalEgresos: number;
  saldoFinal: number;
  cantidadMovimientosTotal?: number;
  cantidadMovimientosIngresos?: number;
  cantidadMovimientosEgresos?: number;
}

export interface MovimientoCaja {
  nroFormulario: number;
  nroTipoFormulario: string;
  nombreTipoFormulario: string;
  fechaEmision: string;
  usuario: string;
  entidad: number;
  nombre: string;
  detalle: string;
  importe: number;
  tipoMovimiento: string;
  mcp: string;
  nombreMCP: string;
  tipoPago: string;
  cartera: number;
  nombreCartera: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
}

export interface DetalleCajaResponse {
  items: MovimientoCaja[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CajaResumen {
  codigoCaja: number;
  descripcion: string;
  estado?: string;
  grupo?: number;
  fechaApertura?: string | null;
  fechaCierre?: string | null;
  grupoNombre?: string | null;
}

export interface ArqueoPorTipoPago {
  tipoPago: string;
  descripcion?: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorMcp {
  mcp: string;
  nombreMcp: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorCartera {
  cartera: number;
  nombreCartera: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorTipoFormulario {
  tipoFormulario: string;
  nombreTipoFormulario: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorUsuario {
  usuario: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorEntidad {
  entidad: number;
  nombre: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoPorFecha {
  fechaEmision: string;
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface InformacionArqueo {
  codigoCaja: number;
  nombreCaja: string | null;
  porTipoPago: ArqueoPorTipoPago[];
  porMcp: ArqueoPorMcp[];
  porCartera: ArqueoPorCartera[];
  porTipoFormulario?: ArqueoPorTipoFormulario[];
  porUsuario?: ArqueoPorUsuario[];
  porEntidad?: ArqueoPorEntidad[];
  porFecha?: ArqueoPorFecha[];
  totalIngresos: number;
  totalEgresos: number;
  saldoFinal: number;
}

export interface CajaSearchParams {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
  estado?: string;
  grupo?: number;
  fechaAperturaDesde?: string;
  fechaAperturaHasta?: string;
  fechaCierreDesde?: string;
  fechaCierreHasta?: string;
  /** Si es true, filtra solo cajas de grupos asignados al usuario. Por defecto false (todas las cajas). */
  soloCajasDelUsuario?: boolean;
}

export interface CajaSearchResponse {
  items: CajaResumen[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CierreCajaRequest {
  codigoCaja: number;
  grupoDestino?: number;
  nroSuc?: number;
}

export interface CierreCajaResponse {
  exito: boolean;
  codigoCaja: number;
  estado: string;
  saldoCaja: number;
  codigoCajaNueva?: number;
  nrosFormularioTransferencia?: number[];
  mensaje?: string;
}

const tesoreriaCajaService = {
  /**
   * Obtiene la información general y resumen de una caja
   * @param codigoCaja - Código de la caja
   * @returns Información de la caja incluyendo saldos calculados
   */
  getInformacionCaja: async (codigoCaja: number): Promise<InformacionCaja> => {
    const response = await api.get<InformacionCaja>(`/api/Caja/${codigoCaja}/informacion`);
    return response.data;
  },

  /**
   * Obtiene los movimientos detallados de una caja (una página específica)
   * @param codigoCaja - Código de la caja
   * @param pageNumber - Número de página (opcional, por defecto 1)
   * @param pageSize - Tamaño de página (opcional, por defecto 50)
   * @param tipoMovimiento - Filtro por tipo: Ingreso o Egreso (opcional)
   * @param fechaDesde - Fecha desde en formato YYYY-MM-DD (opcional)
   * @param fechaHasta - Fecha hasta en formato YYYY-MM-DD (opcional)
   * @returns Lista paginada de movimientos de la caja
   */
  getDetalleCaja: async (
    codigoCaja: number,
    pageNumber: number = 1,
    pageSize: number = 50,
    tipoMovimiento?: 'Ingreso' | 'Egreso',
    fechaDesde?: string,
    fechaHasta?: string
  ): Promise<DetalleCajaResponse> => {
    const params: Record<string, unknown> = {
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    if (tipoMovimiento) {
      params.TipoMovimiento = tipoMovimiento;
    }
    if (fechaDesde) {
      params.FechaDesde = fechaDesde;
    }
    if (fechaHasta) {
      params.FechaHasta = fechaHasta;
    }

    const response = await api.get<DetalleCajaResponse>(`/api/Caja/${codigoCaja}/informaciondetalle`, {
      params,
    });
    return response.data;
  },

  /**
   * Verifica si el usuario actual tiene acceso a una caja
   * Utiliza el endpoint /api/CajaXusuario/caja/{codigoCaja}
   */
  tieneAccesoACaja: async (codigoCaja: number): Promise<boolean> => {
    try {
      const response = await api.get<any>(`/api/CajaXusuario/caja/${codigoCaja}`);
      // Si responde 200 asumimos que tiene acceso
      return response.status === 200;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        // 403/404 -> sin acceso o no existe
        return false;
      }
      // Otros errores se propagan para que el caller los maneje
      throw error;
    }
  },

  /**
   * Busca cajas utilizando el endpoint paginado /api/Caja
   */
  searchCajas: async (params: CajaSearchParams): Promise<CajaSearchResponse> => {
    const response = await api.get<any>('/api/Caja', {
      params: {
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
        SearchTerm: params.searchTerm || undefined,
        Estado: params.estado || undefined,
        Grupo: params.grupo || undefined,
        FechaAperturaDesde: params.fechaAperturaDesde || undefined,
        FechaAperturaHasta: params.fechaAperturaHasta || undefined,
        FechaCierreDesde: params.fechaCierreDesde || undefined,
        FechaCierreHasta: params.fechaCierreHasta || undefined,
        SoloCajasDelUsuario: params.soloCajasDelUsuario ?? false,
      },
    });

    const data = response.data;
    const rawItems: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : [];

    const items: CajaResumen[] = rawItems
      .map((it: any): CajaResumen => {
        const codigoRaw =
          it?.codigoCaja ?? it?.nroCaja ?? it?.codigo ?? it?.id ?? 0;
        const codigoNum = Number(codigoRaw) || 0;

        const descRaw = (it?.descripcion ?? it?.nombre) as string | undefined;
        const descTrim = descRaw && String(descRaw).trim();
        const descripcion =
          descTrim && descTrim.length > 0 ? descTrim : `Caja ${codigoNum}`;

        return {
          codigoCaja: codigoNum,
          descripcion,
          estado: it?.estado,
          grupo: it?.grupo ?? it?.grupoMcp ?? undefined,
          fechaApertura: it?.fechaApertura ?? null,
          fechaCierre: it?.fechaCierre ?? null,
          grupoNombre: it?.grupoNombre ?? null,
        };
      })
      .filter((c) => c.codigoCaja > 0);

    return {
      items,
      totalCount: data?.totalCount ?? items.length,
      pageNumber: data?.pageNumber ?? params.pageNumber,
      pageSize: data?.pageSize ?? params.pageSize,
      totalPages: data?.totalPages ?? 1,
    };
  },

  /**
   * Obtiene la información del arqueo de una caja
   * @param codigoCaja - Código de la caja
   * @returns Información del arqueo agrupada por tipo de pago, MCP y cartera
   */
  getInformacionArqueo: async (codigoCaja: number): Promise<InformacionArqueo> => {
    const response = await api.get<InformacionArqueo>(`/api/Caja/${codigoCaja}/informacionarqueo`);
    return response.data;
  },

  /**
   * Cierra una caja. Si saldo > 0, requiere grupoDestino y nroSuc para transferir.
   */
  cierreCaja: async (request: CierreCajaRequest): Promise<CierreCajaResponse> => {
    const payload = {
      codigoCaja: request.codigoCaja,
      grupoDestino: request.grupoDestino ?? null,
      nroSuc: request.nroSuc ?? null,
    };
    const response = await api.post<CierreCajaResponse>('/api/TesoreriaCaja/cierre-caja', payload);
    const d = response.data as any;
    return {
      exito: d.exito ?? d.Exito ?? false,
      codigoCaja: d.codigoCaja ?? d.CodigoCaja ?? 0,
      estado: d.estado ?? d.Estado ?? '',
      saldoCaja: d.saldoCaja ?? d.SaldoCaja ?? 0,
      codigoCajaNueva: d.codigoCajaNueva ?? d.CodigoCajaNueva,
      nrosFormularioTransferencia: d.nrosFormularioTransferencia ?? d.NrosFormularioTransferencia ?? [],
      mensaje: d.mensaje ?? d.Mensaje,
    };
  },

  /**
   * Descarga el PDF del informe de cierre de caja.
   */
  getCierrePdf: async (codigoCaja: number): Promise<Blob> => {
    const response = await api.get(`/api/Caja/${codigoCaja}/cierre/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default tesoreriaCajaService;
